import {
  AuthenticationResult,
  EventMessage,
  EventType,
  InteractionRequiredAuthError,
  InteractionStatus
} from '@azure/msal-browser'
import { useIsAuthenticated, useMsal } from '@azure/msal-react'
import { JwtPayload, jwtDecode } from 'jwt-decode'
import { Button } from 'primereact/button'
import { FC, useEffect, useState } from 'react'
import { toast } from 'react-hot-toast'
import { Navigate, useLocation, useOutletContext } from 'react-router-dom'
import { useRecoilState, useSetRecoilState } from 'recoil'
import { useGetUserLazy, useUpdateUser } from '../api/user-API'
import { ReactComponent as LogoIcon } from '../assets/svg/gm-logo.svg'
import { protoService } from '../proto/ProtoService'
import { User } from '../types/types'
import {
  AccessTokenKey,
  CurrentUserKey,
  GMMSRaceTeamKey,
  RescaleTokenKey
} from '../utils/constants'
import { appEventInstance } from '../utils/event'
import {
  currentGMIDState,
  currentTeamState,
  currentUserState,
  teamListState
} from '../utils/state-atoms'
import { queryString2JsonString } from '../types/util'

// https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/login-user.md#redirecturi-considerations

export const accessTokenRequest = {
  scopes: [process.env.REACT_APP_AD_CLIENT_SCOPE || '', 'user.read'],
  account: undefined
}

interface IAccessToken extends JwtPayload {
  roles: string[]
}

const savedTeam = localStorage.getItem(GMMSRaceTeamKey)

const Login: FC = () => {
  const { state } = useLocation()
  const { instance: msalInstance, inProgress } = useMsal()
  const isAuthenticated = useIsAuthenticated()
  const [{ loading: loadingUserInfo }, fetchUser] = useGetUserLazy()
  const [tokenIsReady, setTokenIsReady] = useState(false)
  const [userInfoIsReady, setUserInfoIsReady] = useState(false)
  const setCurrentUser = useSetRecoilState(currentUserState)
  const setGMID = useSetRecoilState(currentGMIDState)
  const [, setTeamList] = useRecoilState(teamListState)
  const [, setCurrentTeam] = useRecoilState(currentTeamState)
  const outletSetLoading = useOutletContext() as any
  const location = useLocation()

  // Move setLoading to useEffect to avoid state updates during render
  useEffect(() => {
    outletSetLoading.setLoading(
      !isAuthenticated && inProgress === InteractionStatus.None && !loadingUserInfo
    )
  }, [isAuthenticated, inProgress, loadingUserInfo, outletSetLoading])

  // Register MSAL event callback with proper cleanup
  useEffect(() => {
    const callbackId = msalInstance.addEventCallback((event: EventMessage) => {
      if (event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS && event.payload) {
        console.log('Token is ready:', event)
        const authenticationResult = event.payload as AuthenticationResult
        localStorage.setItem('ACCESS_TOKEN', authenticationResult.accessToken)
        msalInstance.setActiveAccount(authenticationResult.account)
        setTokenIsReady(true)
      }
    })

    return () => {
      if (callbackId) {
        msalInstance.removeEventCallback(callbackId)
      }
    }
  }, [msalInstance])

  useEffect(() => {
    const accounts = msalInstance.getAllAccounts()
    console.log(accounts)
    if (accounts && accounts.length) {
      msalInstance.setActiveAccount(accounts[0])
    } else {
      //
    }
  }, [])

  const setAccountEnv = (userData, teams) => {
    if (userData) {
      if (userData.protoBytesList && userData.protoBytesList.length > 0) {
        const users = userData.protoBytesList.map((item: string) => {
          return protoService.deSerialize(item, 'user')
        })

        // Check for URL parameter from multiple sources (highest priority)
        let teamParam: string | null = null

        // Source 1: Current URL
        const params = new URLSearchParams(location.search)
        teamParam = params.get('raceTeamId')
        console.log('Current URL params=', params.toString())
        console.log('teamParam from current URL=', teamParam)

        // Source 2: Check postLoginRedirect in localStorage (stored by RequireAuth)
        if (!teamParam) {
          const postLoginRedirect = localStorage.getItem('postLoginRedirect')
          console.log('postLoginRedirect=', postLoginRedirect)
          if (postLoginRedirect) {
            // Extract query params from the stored redirect path
            const urlParts = postLoginRedirect.split('?')
            if (urlParts.length > 1) {
              const redirectParams = new URLSearchParams(urlParts[1])
              teamParam = redirectParams.get('raceTeamId')
              console.log('teamParam from postLoginRedirect=', teamParam)
            }
          }
        }

        let validatedUser

        // Priority 1: URL parameter from current URL or postLoginRedirect
        if (teamParam && teams.includes(teamParam)) {
          validatedUser = users.find((user: User) => user.raceTeamId === teamParam)
          if (validatedUser) {
            console.log('Using team from URL parameter:', teamParam)
          }
        }

        // Priority 2: Saved team (if no URL parameter or URL parameter didn't match)
        if (!validatedUser && savedTeam) {
          let userOnSavedTeam
          if (teams.includes(savedTeam)) {
            // saved team is still valid
            userOnSavedTeam = users.filter((user: User) => user.raceTeamId === savedTeam)

            // if no user on the saved team, pick any valid team
            if (userOnSavedTeam.length > 0) {
              validatedUser = userOnSavedTeam[0]
            } else {
              userOnSavedTeam = users.filter((user: User) => teams.includes(user.raceTeamId))
            }
          } else {
            // saved team not valid any more, pick a new default team
            userOnSavedTeam = users.filter((user: User) => teams.includes(user.raceTeamId))
            localStorage.removeItem(GMMSRaceTeamKey)
          }

          if (userOnSavedTeam.length > 0) {
            validatedUser = userOnSavedTeam[0]
          }
        }

        // Priority 3: Default team (if no URL parameter and no saved team)
        if (!validatedUser) {
          // no saved team, pick a default team
          const userOnTeam = users.filter((user: User) => teams.includes(user.raceTeamId))
          if (userOnTeam && userOnTeam.length > 0) {
            validatedUser = userOnTeam[0]
          }
        }

        if (validatedUser) {
          if (!validatedUser.locale) {
            validatedUser.locale = {
              language: 'en-US',
              timeZone: 'America/New_York'
            }
          }

          setCurrentUser(validatedUser)
          setCurrentTeam(validatedUser.raceTeamId)
          appEventInstance.userLoggedIn()
          localStorage.setItem(GMMSRaceTeamKey, validatedUser.raceTeamId)
          localStorage.setItem(CurrentUserKey, JSON.stringify(validatedUser))
          setUserInfoIsReady(true)
          if (validatedUser.rescaleTokens && validatedUser.rescaleTokens.length) {
            localStorage.setItem(RescaleTokenKey, validatedUser.rescaleTokens[0].token)
          } else {
            // Just log it - don't block login or show error
            console.log('Note: No rescale token configured for this user')
          }
        } else {
          // no validated user
          localStorage.removeItem(RescaleTokenKey)
          localStorage.removeItem(GMMSRaceTeamKey)
          localStorage.removeItem(CurrentUserKey)
          toast.error(
            `Your account has not been set up for any race team, please contact the CFD team.`
          )
        }
      } else {
        toast.error('Your account has not been set up, please contact the CFD team.')
      }
    } else {
      toast.error('Your account has not been set up, please contact the CFD team.')
    }
  }

  useEffect(() => {
    if (isAuthenticated) {
      let acct
      if ((acct = msalInstance.getActiveAccount())) {
        console.log('>>>Login Account: ', acct)
        setGMID(acct.username.split('@')[0].toLowerCase())
        msalInstance
          .acquireTokenSilent(accessTokenRequest)
          .then(async accessTokenResponse => {
            // Acquire token silent success
            // Set local storage token
            localStorage.setItem(AccessTokenKey, accessTokenResponse.accessToken)
            const decodedToken = jwtDecode<IAccessToken>(accessTokenResponse.accessToken)

            // Extract possible email addresses
            // SSO username (e.g., TZXDFS@NAM.CORP.GM.COM)
            const ssoUsername = accessTokenResponse.account.username
            // Check if token has actual email (e.g., brandy.paul@gm.com)
            // For @NAM.CORP.GM.COM SSO accounts, preferred_username/upn may match the SSO
            // username exactly, hiding the actual @gm.com address stored in the email field.
            // Prefer any token field that is NOT in the SSO (@NAM.CORP.GM.COM) format so
            // Lookup #4 can fall back to the real @gm.com email stored in the DB.
            const ssoUsernamePattern = /nam\.corp\.gm\.com$/i
            const candidateEmails = [
              (decodedToken as any).email,
              (decodedToken as any).upn,
              (decodedToken as any).preferred_username
            ].filter(Boolean)
            const tokenEmail =
              candidateEmails.find(e => !ssoUsernamePattern.test(e)) || candidateEmails[0]

            // Try to extract teams from token roles (if present)
            let teamsFromToken: string[] = []
            if (decodedToken && decodedToken.roles) {
              const teamFlag = 'RaceTeam.'
              teamsFromToken = decodedToken.roles
                .filter(item => item.startsWith(teamFlag))
                .map(item => {
                  return item.replace(teamFlag, '')
                })
            }

            try {
              const azureOID = accessTokenResponse.account.localAccountId
              const userEmail = accessTokenResponse.account.username

              console.log('=== LOGIN DEBUG ===')
              console.log('SSO Username (userEmail):', userEmail)
              console.log('Azure OID:', azureOID)
              console.log('Token email from JWT:', tokenEmail)
              console.log('tokenEmail === userEmail?', tokenEmail === userEmail)
              console.log('==================')

              // First, try to fetch user by OID (correct way)
              console.log('[Lookup #1] Trying cloudProviderLDAPRef =', azureOID)
              let userRep = await fetchUser(
                'query=' + queryString2JsonString(`cloudProviderLDAPRef=${azureOID}&raceTeamId=all`)
              )
              console.log('[Lookup #1] Result:', userRep.data ? 'FOUND' : 'NOT FOUND')

              // If not found by OID, try fetching by email in cloudProviderLDAPRef (for old records)
              if (
                !userRep.data ||
                !userRep.data.protoBytesList ||
                userRep.data.protoBytesList.length === 0
              ) {
                console.log('[Lookup #2] Trying cloudProviderLDAPRef =', userEmail)
                userRep = await fetchUser(
                  'query=' +
                    queryString2JsonString(`cloudProviderLDAPRef=${userEmail}&raceTeamId=all`)
                )
                console.log(
                  '[Lookup #2] Result:',
                  userRep.data?.protoBytesList?.length ? 'FOUND' : 'NOT FOUND'
                )
              }

              // If still not found, try fetching by actual email field using SSO username
              if (
                !userRep.data ||
                !userRep.data.protoBytesList ||
                userRep.data.protoBytesList.length === 0
              ) {
                console.log('[Lookup #3] Trying email =', userEmail)
                userRep = await fetchUser(
                  'query=' + queryString2JsonString(`email=${userEmail}&raceTeamId=all`)
                )
                console.log(
                  '[Lookup #3] Result:',
                  userRep.data?.protoBytesList?.length ? 'FOUND' : 'NOT FOUND'
                )
              }

              // If still not found and we have a different email from token, try that
              if (
                (!userRep.data ||
                  !userRep.data.protoBytesList ||
                  userRep.data.protoBytesList.length === 0) &&
                tokenEmail &&
                tokenEmail !== userEmail
              ) {
                console.log('[Lookup #4] Trying email =', tokenEmail)
                userRep = await fetchUser(
                  'query=' + queryString2JsonString(`email=${tokenEmail}&raceTeamId=all`)
                )
                console.log(
                  '[Lookup #4] Result:',
                  userRep.data?.protoBytesList?.length ? 'FOUND' : 'NOT FOUND'
                )
              }

              // Check if user was found by any of the lookup methods
              if (
                !userRep.data ||
                !userRep.data.protoBytesList ||
                userRep.data.protoBytesList.length === 0
              ) {
                console.error(
                  `User not found in database. Email: ${userEmail}, Azure OID: ${azureOID}`
                )
                toast.error(
                  `Your account (${userEmail}) has not been created in the Aero Platform. Please contact the CFD team to have your account set up.`
                )
                return
              }

              // If found by email lookup (either cloudProviderLDAPRef or email field), migrate to use OID
              if (
                userRep.data &&
                userRep.data.protoBytesList &&
                userRep.data.protoBytesList.length > 0
              ) {
                const usersToCheck = userRep.data.protoBytesList.map((item: string) => {
                  return protoService.deSerialize(item, 'user')
                })

                // Check if any user needs OID migration (including users with no/blank cloudProviderLDAPRef)
                const usersNeedingMigration = usersToCheck.filter(
                  (user: any) => user.cloudProviderLDAPRef !== azureOID
                )

                if (usersNeedingMigration.length > 0) {
                  // Update each user record with the correct OID
                  for (const user of usersNeedingMigration) {
                    try {
                      // Update the cloudProviderLDAPRef field
                      user.cloudProviderLDAPRef = azureOID

                      // Build the update request body with all required fields
                      const updateBody = {
                        firstName: user.firstName,
                        lastName: user.lastName,
                        email: user.email,
                        raceTeamId: user.raceTeamId,
                        role: {
                          roleId: user.role?.roleId || 'read-only',
                          name: user.role?.name || 'Read-Only'
                        },
                        rescaleTokens: user.rescaleTokens || [],
                        cloudProviderLDAPRef: azureOID,
                        updatedByUser: user.email // Self-update during login migration
                      }

                      const response = await fetch(
                        `${process.env.REACT_APP_API_BASE_URL}/Users/${user.email}`,
                        {
                          method: 'PUT',
                          headers: {
                            'Content-Type': 'application/json',
                            Authorization: `Bearer ${accessTokenResponse.accessToken}`,
                            gmmsraceteamid: user.raceTeamId
                          },
                          body: JSON.stringify(updateBody)
                        }
                      )

                      if (!response.ok) {
                        const error = await response.text()
                        console.error(
                          `Failed to update user ${user._id}: ${response.status} ${error}`
                        )
                      }
                    } catch (updateError) {
                      console.error(`Failed to update user ${user._id}:`, updateError)
                    }
                  }

                  // Re-fetch with OID after migration
                  userRep = await fetchUser(
                    'query=' +
                      queryString2JsonString(`cloudProviderLDAPRef=${azureOID}&raceTeamId=all`)
                  )

                  // Verify the re-fetch was successful
                  if (
                    !userRep.data ||
                    !userRep.data.protoBytesList ||
                    userRep.data.protoBytesList.length === 0
                  ) {
                    console.error(
                      'Re-fetch after OID migration failed. User will need to log out and back in.'
                    )
                    toast.error(
                      'Your account has been updated. Please log out and back in to continue.'
                    )
                    return
                  }
                }
              }

              // Determine which teams to use — DB records are the source of truth for
              // authorization.  Token roles (teamsFromToken) are only used as a last
              // resort so that a misconfigured DB doesn't lock everyone out.
              let teams: string[]
              if (
                userRep.data &&
                userRep.data.protoBytesList &&
                userRep.data.protoBytesList.length > 0
              ) {
                // Always derive the list from DB records — only teams the user
                // actually has a record for should appear in the dropdown.
                const users = userRep.data.protoBytesList.map((item: string) =>
                  protoService.deSerialize(item, 'user')
                )
                teams = [...new Set(users.map((u: any) => u.raceTeamId))] as string[]
              } else if (teamsFromToken.length > 0) {
                // Fallback: no DB records found but token has role assignments
                console.warn(
                  'No DB user records found — falling back to Azure token roles for team list'
                )
                teams = teamsFromToken
              } else {
                console.error('No user records found and no teams in token')
                toast.error('No race team assigned to your account, please contact the CFD team.')
                return
              }

              setTeamList(teams)
              setAccountEnv(userRep.data, teams)
            } catch (error) {
              console.error('Error fetching user info:', error)
              toast.error(
                'Error loading user info. Please contact the CFD team if this issue persists.'
              )
            }
          })
          .catch(async err => {
            // Handle various auth errors that require interactive login
            const errorName = err?.name || ''
            const errorMessage = err?.message || ''
            const requiresInteraction =
              err instanceof InteractionRequiredAuthError ||
              errorName === 'BrowserAuthError' ||
              errorMessage.includes('monitor_window_timeout') ||
              errorMessage.includes('iframe') ||
              errorMessage.includes('interaction_required')

            if (requiresInteraction) {
              console.log('Silent token acquisition failed, falling back to redirect...', err)
              // fallback to interaction when silent call fails
              await msalInstance.acquireTokenRedirect(accessTokenRequest)
            } else {
              console.error('Unexpected auth error:', err)
              toast.error('Error logging in. Please try refreshing the page.')
            }
          })
      }
    }
  }, [isAuthenticated])

  console.log('[Login] Auth status:', isAuthenticated, tokenIsReady, userInfoIsReady)
  if (isAuthenticated && tokenIsReady && userInfoIsReady) {
    // Check for saved redirect path
    const postLoginRedirect = localStorage.getItem('postLoginRedirect')
    console.log('[Login] postLoginRedirect from localStorage:', postLoginRedirect)
    console.log('[Login] state object:', state)
    console.log('[Login] state.ref:', state?.ref)

    if (postLoginRedirect) {
      localStorage.removeItem('postLoginRedirect')
      console.log('[Login] >>>Logged in and navigate to saved redirect:', postLoginRedirect)
      return <Navigate to={postLoginRedirect} replace={true} />
    }
    const fallbackPath = (state && state.ref) || '/'
    console.log('[Login] >>>Logged in and navigate to fallback:', fallbackPath)
    return <Navigate to={fallbackPath} replace={true} />
  }

  const handleLogin = async () => {
    try {
      await msalInstance.loginRedirect()
    } catch (error) {
      console.log(error)
    }
  }

  return (
    <div className="basis-0 grow flex flex-col items-center justify-center space-y-4 bg-white py-20 mx-auto max-w-[600px]">
      {!isAuthenticated && inProgress === InteractionStatus.None && !loadingUserInfo && (
        <>
          <LogoIcon className="w-20 h-20 fill-gm-blue" />
          <h1 className="text-gm-blue text-xl font-semibold">AERO DESIGN</h1>
          <div className="pt-4">Login to continue</div>
          <Button className="px-14" onClick={handleLogin} label="Login Now" />
        </>
      )}
    </div>
  )
}

export default Login
